import { httpClient } from '@/utils/httpClient';
import { formatNomenclatureWithSize } from '@equip/utils';
import { outputReport, getOutputFormat, getPreOpenedWindow } from '@/utils/reportOutput';
import { getPersonLabels } from '@/hooks/usePersonLabels';
import {
  buildExpiredAssetsReportHtml,
  type ExpiredAssetsItem,
  type ExpiredAssetsTemplateOptions,
} from '@/reports/templates/expiredAssetsTemplate';
import { entityService } from '@/services/EntityService';

interface AssetEntityRow {
  id: string;
  display_name: string;
  entity_data: {
    lin?: string;
    nsn?: string;
    size?: string;
    expiration_date?: string;
    squadron_section?: string;
  };
}

const chunk = <T>(values: T[], size: number): T[][] => {
  if (size <= 0) return [values];
  const chunks: T[][] = [];
  for (let i = 0; i < values.length; i += size) {
    chunks.push(values.slice(i, i + size));
  }
  return chunks;
};

const normalizeDateOnly = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  const date = new Date(trimmed);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString().slice(0, 10);
};

export const runExpiredAssetsReport = async (params: Record<string, unknown>): Promise<void> => {
  const expiringByDate = normalizeDateOnly(params.expiringByDate);

  const query = new URLSearchParams({
    entity_type: 'eq.asset',
    select: 'id,display_name,entity_data',
  }).toString();

  const rows = await httpClient.get<AssetEntityRow[]>(`/api/rest/data/v1/entities?${query}`);

  const today = new Date();
  const todayStart = new Date(
    Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()),
  );
  const cutoffDate = expiringByDate ? new Date(expiringByDate) : todayStart;
  const cutoffMs = Math.min(cutoffDate.getTime(), todayStart.getTime()) + 24 * 3600 * 1000 - 1;

  // Fetch active hand receipts to get asset → person mapping (assets don't have person_id directly)
  const personIdByAssetId = new Map<string, string>();
  const hrQuery = new URLSearchParams({
    select: 'entity_data',
    entity_type: 'eq.hand_receipt',
    status: 'eq.active',
    is_current: 'eq.true',
    limit: '10000',
  }).toString();

  const handReceipts = await httpClient.get<
    Array<{ entity_data: { asset_id?: string; person_id?: string } }>
  >(`/api/rest/data/v1/entities?${hrQuery}`);

  for (const hr of handReceipts) {
    const assetId = hr.entity_data?.asset_id;
    const personId = hr.entity_data?.person_id;
    if (assetId && personId) {
      personIdByAssetId.set(assetId, personId);
    }
  }

  const personIds = Array.from(new Set(personIdByAssetId.values()));

  const personNameById = new Map<string, string>();
  for (const batchIds of chunk(personIds, 75)) {
    const idFilter = batchIds.length === 1 ? `eq.${batchIds[0]}` : `in.(${batchIds.join(',')})`;
    const people = await entityService.list({
      type: 'person',
      limit: batchIds.length,
      order: 'id.asc',
      filters: {
        id: idFilter,
        select: 'id,display_name',
      },
    });
    for (const person of people) {
      personNameById.set(person.id, person.display_name);
    }
  }

  const grouped = new Map<string, ExpiredAssetsItem>();

  for (const row of rows) {
    const expiration = row.entity_data.expiration_date ?? null;
    if (!expiration) continue;

    const expDate = new Date(expiration);
    if (Number.isNaN(expDate.getTime())) continue;

    if (expDate.getTime() > cutoffMs) {
      continue;
    }

    const personId = personIdByAssetId.get(row.id) ?? null;
    const individual = personId ? (personNameById.get(personId) ?? null) : null;

    const key = [
      row.entity_data.lin ?? '',
      row.entity_data.nsn ?? '',
      row.display_name ?? '',
      expiration,
      row.entity_data.squadron_section ?? '',
      personId ?? '',
    ].join('|');

    const existing = grouped.get(key);
    if (existing) {
      existing.quantity = (existing.quantity ?? 0) + 1;
      continue;
    }

    grouped.set(key, {
      lin: row.entity_data.lin ?? null,
      nsn: row.entity_data.nsn ?? null,
      nomenclature: formatNomenclatureWithSize(
        row.display_name,
        row.entity_data.size as string | undefined,
      ),
      squadronOrSection: row.entity_data.squadron_section ?? null,
      expirationDate: expiration,
      quantity: 1,
      individual,
    });
  }

  const items = Array.from(grouped.values()).sort((a, b) => {
    const dateA = a.expirationDate ? new Date(a.expirationDate).getTime() : 0;
    const dateB = b.expirationDate ? new Date(b.expirationDate).getTime() : 0;
    if (dateA !== dateB) return dateA - dateB;
    return (a.nomenclature ?? '').localeCompare(b.nomenclature ?? '');
  });

  const options: ExpiredAssetsTemplateOptions = {
    personLabels: getPersonLabels(params),
    generatedAt: new Date(),
    header: {
      expiringByDate: expiringByDate ?? todayStart.toISOString().slice(0, 10),
    },
    items,
  };

  const html = buildExpiredAssetsReportHtml(options);
  outputReport(
    html,
    'expired-assets-report.html',
    getOutputFormat(params),
    getPreOpenedWindow(params),
  );
};
